import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchStates } from '../data-provider/states';
import type { State, States, StatesParams } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

export const statesQueryOptions = (params: StatesParams, { enabled = true }: { enabled?: boolean } = {}) =>
  queryOptions({
    enabled: enabled && Boolean(params.CountryCode),
    queryFn: async ({ signal }) => {
      const states = await fetchStates(params, { signal });
      put(states.list, params.CountryCode);
      return states;
    },
    queryKey: CACHE_KEYS.states(params),
    staleTime: 1000 * 60 * 60,
  });

const getStatesCacheKey = (countryCode: string) => statesQueryOptions({ CountryCode: countryCode }).queryKey;

const getStateFallback = (stateId: string): State => {
  logger.warn(`State "${stateId}" was not found. Verify the data model.`);

  return {
    id: stateId,
    label: stateId,
  };
};

export const put = (states: State[], countryCode: string) => {
  const data = queryClient.getQueryData<States>(getStatesCacheKey(countryCode));

  const newStates = states.reduce(
    (acc, state) => {
      acc[String(state.id)] = state;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(getStatesCacheKey(countryCode), {
    list: Object.values(newStates),
    map: newStates,
  });
};

export const getAll = (countryCode: string): States | undefined => {
  return queryClient.getQueryData<States>(getStatesCacheKey(countryCode));
};

export const find = async (stateId: string, countryCode: string): Promise<State> => {
  const data = queryClient.getQueryData<States>(getStatesCacheKey(countryCode));
  const state = data?.map[stateId];

  if (state) {
    return state;
  }

  try {
    const newData = await queryClient.query(statesQueryOptions({ CountryCode: countryCode }));
    if (newData.map[stateId]) {
      put(newData.list, countryCode);
      return newData.map[stateId];
    }
  } catch {
    return getStateFallback(stateId);
  }

  return getStateFallback(stateId);
};
